type FormulaValues = Record<string, number>

export class FormulaCalculator {
  private readonly formula: string
  private readonly values: FormulaValues
  private readonly pendingFormulas: ReadonlySet<string>

  constructor(
    formula: string,
    values: FormulaValues,
    pendingFormulas: ReadonlySet<string>
  ) {
    this.formula = formula.replace(/\s+/g, '') // Remove all whitespace
    this.values = values
    this.pendingFormulas = pendingFormulas
  }

  calculate(): number | null {
    const substitutedFormula = this.substituteReferences()

    if (
      substitutedFormula === null ||
      !this.isSupportedFormula(substitutedFormula)
    ) {
      return null
    }

    try {
      // Safe: all named references have been substituted with numbers by substituteReferences(),
      // and isSupportedFormula() ensures only digits, arithmetic operators and parentheses remain
      // before we reach this point — no arbitrary code can be injected.
      // eslint-disable-next-line @typescript-eslint/no-implied-eval, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const result = Function(`"use strict"; return (${substitutedFormula});`)()

      return typeof result === 'number' && Number.isFinite(result)
        ? result
        : null
    } catch {
      return null
    }
  }

  private substituteReferences(): string | null {
    let hasPendingReference = false

    const substituted = this.formula.replace(
      /\b[a-zA-Z_]\w*\b/g,
      (reference) => {
        const value = this.values[reference]

        if (typeof value === 'number' && Number.isFinite(value)) {
          return `(${value})`
        }

        if (this.pendingFormulas.has(reference)) {
          hasPendingReference = true
          return reference
        }

        return '(0)'
      }
    )

    return hasPendingReference ? null : substituted
  }

  private isSupportedFormula(formula: string): boolean {
    // Allowed characters: numbers, +, -, *, /, (), decimal points
    return /^[\d+\-*/().]+$/.test(formula)
  }
}
